import type { Velocity } from '@/components'

/**
 * Holds a body against a point instead of merely pulling at it.
 *
 * A pull written as acceleration cannot win. Our strongest field pulls at
 * 30 m/s² against a fighter who accelerates at 62 — the victim simply walks out
 * at twice the rate they are dragged, which is why the Vortex has never held
 * anybody in the whole history of this project. `docs/PHYSICS.md` §6.6 asked for
 * this a long time ago and it was never built:
 *
 *   > притяжение **обязано побеждать ходьбу**: не ускорение, а ограничение
 *   > исходящей скорости (`grip`)
 *
 * So: the component of velocity pointing *away* from the anchor is capped, and
 * a gentle acceleration inward is added on top. Walking out is still possible —
 * it is just slow, and slow is what makes the field a threat rather than a
 * suggestion. Movement across the field and toward it are untouched, so the
 * victim keeps every option except the one the ability exists to take away.
 *
 * Returns the outgoing speed after the clamp, which is what `simcheck` measures.
 */
export function applyGrip(
  vel: ReturnType<typeof Velocity.create>,
  towardX: number,
  towardZ: number,
  /** Fastest the body may still travel away from the anchor, m/s. */
  maxOutgoing: number,
  /** Steady acceleration toward the anchor, m/s². */
  inward: number,
  dt: number,
): number {
  const len = Math.hypot(towardX, towardZ)
  if (len < 1e-5) return Math.hypot(vel.x, vel.z)
  const nx = towardX / len
  const nz = towardZ / len

  // Positive means moving toward the anchor; negative means escaping.
  const along = vel.x * nx + vel.z * nz
  const outgoing = -along

  if (outgoing > maxOutgoing) {
    // Remove only the excess, and only along the escape axis: sideways motion
    // is the victim's own, and taking it would make the field a stun.
    const excess = outgoing - maxOutgoing
    vel.x += nx * excess
    vel.z += nz * excess
  }

  vel.x += nx * inward * dt
  vel.z += nz * inward * dt

  return Math.max(0, -(vel.x * nx + vel.z * nz))
}
